import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import { gscDetailColumnConfig } from '@/app/KeywordResearch/config/gscDetailColumnConfig';
import { Col, Row, Spinner } from 'react-bootstrap';
import MetricLineTimeChartSkeleton from '@/app/common/components/skeleton/MetricLineTimeChartSkeleton';
import IconAlert from '@/components/common/IconAlert';
import { useLocation, useOutletContext, useParams } from 'react-router-dom';
import {
  useCountKeywordHistoryQuery,
  useExportTableMutation,
  useGetDateChartQuery,
  useGetDateTotalsQuery,
  useGetKeywordHistoryMutation
} from '@/app/KeywordResearch/api/gscApi';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import FiltersComponentSkeleton from '@/app/common/components/filters/FiltersComponentSkeleton';
import MetricLineTimeChart, {
  type MetricLineTimeChartDateRange
} from '@/app/common/components/charts/MetricLineTimeChart';
import { mapGscDateChartData } from '@/app/KeywordResearch/utils/mapGscDateChartData';
import type { GscHistoryStatus } from '@/app/KeywordResearch/types/gscApiTypes';

const PENDING_CHART_STATUSES: GscHistoryStatus[] = [
  'inserted',
  'processing',
  'queued'
];

const isPendingChartStatus = (status?: string | null) =>
  Boolean(status && PENDING_CHART_STATUSES.includes(status as GscHistoryStatus));

type BreadcrumbItem = { text: string; href: string };

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

type AgGridWrapperProps = {
  table_name?: string;
  height: string;
  columns: unknown[];
  data?: unknown[];
  total?: number;
  page_depth?: number;
  fetchFunction?: unknown;
  additionalData?: Record<string, unknown>;
  settings?: Record<string, unknown>;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

function KeywordsGscDetails() {
  const gridRef = useRef<HTMLDivElement>(null);
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const [getKeywordsHistory] = useGetKeywordHistoryMutation();
  const [exportTable] = useExportTableMutation();
  const [dateRange, setDateRange] =
    useState<MetricLineTimeChartDateRange | null>(null);

  const [chartPollingInterval, setChartPollingInterval] = useState(0);

  const { data: chartResponse, isFetching: isChartFetching } =
    useGetDateChartQuery(slug ?? '', {
      skip: !slug,
      pollingInterval: chartPollingInterval
    });

  const { data: totalsResponse, isFetching: isTotalsFetching } =
    useGetDateTotalsQuery(
      {
        host_name_id: slug ?? '',
        ...(dateRange
          ? {
              start_date: dateRange.start_date,
              end_date: dateRange.end_date
            }
          : {})
      },
      { skip: !slug }
    );

  const isChartDataEmpty = useMemo(() => {
    if (!chartResponse?.data) {
      return false;
    }

    return chartResponse.data.dates.length === 0;
  }, [chartResponse]);

  const chartStatus = chartResponse?.data?.status;

  const isChartDataPending = useMemo(
    () => isChartDataEmpty && isPendingChartStatus(chartStatus),
    [isChartDataEmpty, chartStatus]
  );

  const isChartPermissionDenied = useMemo(
    () => chartStatus === 'permission_denied',
    [chartStatus]
  );

  const isChartDataFinishedEmpty = useMemo(
    () => isChartDataEmpty && chartStatus === 'finished',
    [isChartDataEmpty, chartStatus]
  );

  useEffect(() => {
    const shouldPoll =
      chartResponse?.data?.dates?.length === 0 &&
      isPendingChartStatus(chartResponse?.data?.status);

    setChartPollingInterval(shouldPoll ? 10000 : 0);
  }, [chartResponse]);

  const chartData = useMemo(
    () =>
      chartResponse?.data && totalsResponse?.data && !isChartDataEmpty
        ? mapGscDateChartData(chartResponse.data, totalsResponse.data)
        : null,
    [chartResponse, totalsResponse, isChartDataEmpty]
  );

  const isChartLoading =
    (isChartFetching && !chartResponse?.data) ||
    (!isChartDataEmpty &&
      !isChartDataPending &&
      isTotalsFetching &&
      !chartData);

  const handleTimeRangeChange = useCallback(
    (range: MetricLineTimeChartDateRange) => {
      setDateRange(range);
    },
    []
  );

  useEffect(() => {
    setBreadcrumbData([{ text: 'کلمات GSC', href: '#' }]);
  }, [setBreadcrumbData]);

  const { data, isFetching } = useCountKeywordHistoryQuery(slug ?? '', {
    skip: !slug
  });

  return (
    <Row>
      <Col xxl={12} className="my-3">
        {isChartLoading ? (
          <MetricLineTimeChartSkeleton />
        ) : isChartDataPending ? (
          <IconAlert variant="info" className="mb-0">
            <span className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              در حال دریافت اطلاعات لطفا منتظر بمانید
            </span>
          </IconAlert>
        ) : isChartPermissionDenied ? (
          <IconAlert variant="warning" className="mb-0">
            عدم دسترسی
          </IconAlert>
        ) : isChartDataFinishedEmpty ? (
          <IconAlert variant="info" className="mb-0">
            داده‌ای برای نمایش نمودار وجود ندارد
          </IconAlert>
        ) : (
          chartData && (
            <MetricLineTimeChart
              dates={chartData.dates}
              metrics={chartData.metrics}
              defaultActiveIds={['clicks', 'impressions']}
              topLabel="عملکرد سرچ کنسول"
              onTimeRangeChange={handleTimeRangeChange}
            />
          )
        )}
      </Col>
      {!isFetching ? (
        <>
          <Col xxl={12}>
            <FiltersComponent columns={gscDetailColumnConfig} />
          </Col>
          <Col>
            <div ref={gridRef}>
              <TypedAgGridWrapper
                table_name={location.pathname.split('/').slice(1, 3).join('/')}
                settings={{
                  pagination: true,
                  export: {
                    additionalData: { host_name_id: slug },
                    enabled: true,
                    fetchFunction: exportTable
                  }
                }}
                fetchFunction={getKeywordsHistory}
                height="700px"
                additionalData={{
                  host_name_id: slug
                }}
                total={data?.data?.total}
                data={data?.data?.rows}
                columns={gscDetailColumnConfig}
                page_depth={1}
              />
            </div>
          </Col>
        </>
      ) : (
        <>
          <Col xxl={12} className="mb-2">
            <FiltersComponentSkeleton />
          </Col>
          <Col xxl={12}>
            <AgGridSkeleton
              height="550px"
              settings={{
                pagination: false,
                fullscreen: false,
                changeHeight: false,
                resetGrid: false,
                saveGrid: false,
                exportBtn: false
              }}
            />
          </Col>
        </>
      )}
    </Row>
  );
}

export default KeywordsGscDetails;
